import {
  FilePreflightError,
  preflightRasterFile,
  type DecodedImage,
  type ImageMeta,
} from '@/shared/image/filePreflight'

export type FitMode = 'contain' | 'cover'
export type LoopMode = 'once' | 'forever' | 'repeat'

export interface CanvasSize {
  width: number
  height: number
}

export interface GifDraftFrame {
  file: File
  meta: ImageMeta
  delayMs: number | null
}

export interface GifSettings {
  size: CanvasSize
  fit: FitMode
  transparent: boolean
  background: string
  globalDelayMs: number
  loopMode: LoopMode
  repeatCount: number
}

export interface DrawGeometry {
  sourceX: number
  sourceY: number
  sourceWidth: number
  sourceHeight: number
  destinationX: number
  destinationY: number
  destinationWidth: number
  destinationHeight: number
}

export type GifGuardrailCode =
  | 'frame_count'
  | 'source_limit'
  | 'canvas_limit'
  | 'aggregate_limit'
  | 'invalid_delay'
  | 'invalid_repeat'

export class GifGuardrailError extends Error {
  constructor(readonly code: GifGuardrailCode) {
    super(code)
    this.name = 'GifGuardrailError'
  }
}

export const MIN_FRAMES = 2
export const MAX_FRAMES = 20
export const MAX_SOURCE_BYTES = 15 * 1024 * 1024
export const MAX_SOURCE_PIXELS = 16_000_000
export const MAX_CANVAS_SIDE = 1280
export const MAX_FRAME_PIXELS = 1_000_000
export const MAX_AGGREGATE_PIXELS = 12_000_000
export const MAX_OUTPUT_BYTES = 32 * 1024 * 1024
export const MIN_DELAY_MS = 100
export const MAX_DELAY_MS = 10_000
export const DELAY_STEP_MS = 10
export const MIN_REPEAT = 2
export const MAX_REPEAT = 20
export const PALETTE_LIMIT = 255
export const GENERATION_TIMEOUT_MS = 45_000

const PREFLIGHT_LIMITS = {
  maxFileBytes: MAX_SOURCE_BYTES,
  maxImagePixels: MAX_SOURCE_PIXELS,
} as const

export { FilePreflightError }
export type { DecodedImage, ImageMeta }

export async function inspectGifSource(
  file: File,
  decode: (source: ImageBitmapSource) => Promise<DecodedImage> = createImageBitmap,
): Promise<ImageMeta> {
  const header = new Uint8Array(await file.slice(0, 3).arrayBuffer())
  const isJpeg = header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff
  const meta = await preflightRasterFile(file, PREFLIGHT_LIMITS)
  let bitmap: DecodedImage
  try {
    bitmap = await decode(file)
  } catch {
    throw new FilePreflightError('decode_failed')
  }
  try {
    const exact = bitmap.width === meta.width && bitmap.height === meta.height
    const oriented = isJpeg && bitmap.width === meta.height && bitmap.height === meta.width
    const decodedPixels = bitmap.width * bitmap.height
    if ((!exact && !oriented) || !Number.isSafeInteger(decodedPixels) || decodedPixels > MAX_SOURCE_PIXELS) {
      throw new FilePreflightError('invalid_container')
    }
    return { width: bitmap.width, height: bitmap.height, bytes: meta.bytes }
  } finally {
    bitmap.close()
  }
}

export function moveFrame<T>(frames: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= frames.length || to >= frames.length) {
    return [...frames]
  }
  const next = [...frames]
  const [frame] = next.splice(from, 1)
  if (frame !== undefined) next.splice(to, 0, frame)
  return next
}

export function effectiveDelay(frame: Pick<GifDraftFrame, 'delayMs'>, globalDelayMs: number): number {
  return frame.delayMs ?? globalDelayMs
}

export function previewDelayMs(requestedDelayMs: number): number {
  if (!Number.isFinite(requestedDelayMs)) return MIN_DELAY_MS
  return Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, requestedDelayMs))
}

export function isValidDelay(delayMs: number): boolean {
  return Number.isInteger(delayMs)
    && delayMs >= MIN_DELAY_MS
    && delayMs <= MAX_DELAY_MS
    && delayMs % DELAY_STEP_MS === 0
}

export function gifRepeatValue(mode: LoopMode, repeatCount: number): number {
  if (mode === 'once') return -1
  if (mode === 'forever') return 0
  if (!Number.isInteger(repeatCount) || repeatCount < MIN_REPEAT || repeatCount > MAX_REPEAT) {
    throw new GifGuardrailError('invalid_repeat')
  }
  return repeatCount
}

export function validateGifDraft(frames: readonly GifDraftFrame[], settings: GifSettings): void {
  if (frames.length < MIN_FRAMES || frames.length > MAX_FRAMES) {
    throw new GifGuardrailError('frame_count')
  }
  for (const frame of frames) {
    const pixels = frame.meta.width * frame.meta.height
    if (
      frame.meta.bytes > MAX_SOURCE_BYTES
      || !Number.isSafeInteger(pixels)
      || pixels < 1
      || pixels > MAX_SOURCE_PIXELS
    ) throw new GifGuardrailError('source_limit')
    if (!isValidDelay(effectiveDelay(frame, settings.globalDelayMs))) {
      throw new GifGuardrailError('invalid_delay')
    }
  }
  const { width, height } = settings.size
  const framePixels = width * height
  if (
    !Number.isInteger(width)
    || !Number.isInteger(height)
    || width < 1
    || height < 1
    || Math.max(width, height) > MAX_CANVAS_SIDE
    || !Number.isSafeInteger(framePixels)
    || framePixels > MAX_FRAME_PIXELS
  ) throw new GifGuardrailError('canvas_limit')
  if (framePixels * frames.length > MAX_AGGREGATE_PIXELS) {
    throw new GifGuardrailError('aggregate_limit')
  }
  gifRepeatValue(settings.loopMode, settings.repeatCount)
}

export function frameGeometry(
  source: CanvasSize,
  destination: CanvasSize,
  fit: FitMode,
): DrawGeometry {
  if (
    source.width <= 0 || source.height <= 0
    || destination.width <= 0 || destination.height <= 0
  ) throw new GifGuardrailError('canvas_limit')

  const scale = fit === 'contain'
    ? Math.min(destination.width / source.width, destination.height / source.height)
    : Math.max(destination.width / source.width, destination.height / source.height)
  const scaledWidth = source.width * scale
  const scaledHeight = source.height * scale

  if (fit === 'contain') {
    return {
      sourceX: 0,
      sourceY: 0,
      sourceWidth: source.width,
      sourceHeight: source.height,
      destinationX: (destination.width - scaledWidth) / 2,
      destinationY: (destination.height - scaledHeight) / 2,
      destinationWidth: scaledWidth,
      destinationHeight: scaledHeight,
    }
  }

  return {
    sourceX: Math.max(0, (source.width - destination.width / scale) / 2),
    sourceY: Math.max(0, (source.height - destination.height / scale) / 2),
    sourceWidth: destination.width / scale,
    sourceHeight: destination.height / scale,
    destinationX: 0,
    destinationY: 0,
    destinationWidth: destination.width,
    destinationHeight: destination.height,
  }
}

export function validateGifOutputSize(byteLength: number): void {
  if (!Number.isSafeInteger(byteLength) || byteLength < 0 || byteLength > MAX_OUTPUT_BYTES) {
    throw new Error('output_too_large')
  }
}

export interface ParsedGifMetadata {
  width: number
  height: number
  frameDelaysMs: number[]
  repeat: number | null
  transparentFrames: boolean[]
  maximumPaletteSize: number
}

function readU16(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8)
}

function skipSubBlocks(bytes: Uint8Array, start: number): number {
  let offset = start
  let blocks = 0
  while (offset < bytes.length) {
    if (++blocks > 65_536) throw new Error('invalid_gif')
    const size = bytes[offset] ?? 0
    offset += 1
    if (size === 0) return offset
    offset += size
    if (offset > bytes.length) throw new Error('invalid_gif')
  }
  throw new Error('invalid_gif')
}

export function parseGifMetadata(bytes: Uint8Array): ParsedGifMetadata {
  if (bytes.length < 14 || String.fromCharCode(...bytes.subarray(0, 6)) !== 'GIF89a') {
    throw new Error('invalid_gif')
  }
  const width = readU16(bytes, 6)
  const height = readU16(bytes, 8)
  const packed = bytes[10] ?? 0
  let maximumPaletteSize = 0
  let offset = 13
  if ((packed & 0x80) !== 0) {
    const size = 2 ** ((packed & 0x07) + 1)
    maximumPaletteSize = size
    offset += size * 3
  }
  const frameDelaysMs: number[] = []
  const transparentFrames: boolean[] = []
  let repeat: number | null = null
  let pendingDelay = 0
  let pendingTransparent = false
  let structures = 0
  while (offset < bytes.length) {
    if (++structures > 131_072) throw new Error('invalid_gif')
    const marker = bytes[offset++]
    if (marker === 0x3b) {
      return { width, height, frameDelaysMs, repeat, transparentFrames, maximumPaletteSize }
    }
    if (marker === 0x21) {
      const label = bytes[offset++]
      if (label === 0xf9) {
        if (bytes[offset++] !== 4 || offset + 5 > bytes.length) throw new Error('invalid_gif')
        const graphicPacked = bytes[offset] ?? 0
        pendingTransparent = (graphicPacked & 1) !== 0
        pendingDelay = readU16(bytes, offset + 1) * 10
        offset += 4
        if (bytes[offset++] !== 0) throw new Error('invalid_gif')
      } else if (label === 0xff) {
        const blockSize = bytes[offset++] ?? 0
        if (blockSize !== 11 || offset + 11 > bytes.length) throw new Error('invalid_gif')
        const app = String.fromCharCode(...bytes.subarray(offset, offset + 11))
        offset += 11
        if (app === 'NETSCAPE2.0' && bytes[offset] === 3 && bytes[offset + 1] === 1) {
          repeat = readU16(bytes, offset + 2)
        }
        offset = skipSubBlocks(bytes, offset)
      } else {
        offset = skipSubBlocks(bytes, offset)
      }
      continue
    }
    if (marker === 0x2c) {
      if (offset + 9 > bytes.length) throw new Error('invalid_gif')
      const imagePacked = bytes[offset + 8] ?? 0
      offset += 9
      if ((imagePacked & 0x80) !== 0) {
        const size = 2 ** ((imagePacked & 0x07) + 1)
        maximumPaletteSize = Math.max(maximumPaletteSize, size)
        offset += size * 3
      }
      if (offset >= bytes.length) throw new Error('invalid_gif')
      offset += 1
      offset = skipSubBlocks(bytes, offset)
      frameDelaysMs.push(pendingDelay)
      transparentFrames.push(pendingTransparent)
      pendingDelay = 0
      pendingTransparent = false
      continue
    }
    throw new Error('invalid_gif')
  }
  throw new Error('invalid_gif')
}
